import { useSelector } from 'react-redux';
import Page from '../../components/Page';
import { RootState, store } from '../../store/store';
import { examsSelectors } from '../../slices/exams/examsSlice';
import { useEffect, useRef, useState } from 'react';
import { useRefreshStudySessionAndContent } from '../../utils/refreshStudySession';
import { useNavigate } from 'react-router-dom';
import { useHotkeys } from 'react-hotkeys-hook';
import { getExamLectures } from '../../services/exams';
import { clearLectures } from '../../slices/lectures/lecturesSlice';
import InputLabel from '@mui/material/InputLabel';
import SchoolSelector from '../../components/Global/SchoolSelector';
import LectureScheduleEditor from '../../components/ExamEditor/LectureScheduleEditor';
import SchoolExamInfo from '../../components/ExamEditor/SchoolExamInfo';

interface ExamEditorProps {
  isCreateMode: boolean;
}

const ExamEditor: React.FC<ExamEditorProps> = ({ isCreateMode }) => {
  const userSchool = useSelector((state: RootState) => state.school.school);
  const todaySession = useSelector((state: RootState) => state.todaySession);
  const primaryExam = useSelector(examsSelectors.selectPrimaryExam);

  const [examCreated, setExamCreated] = useState<boolean>(false);
  const [shouldRefreshStudySession, setShouldRefreshStudySession] =
    useState<boolean>(false);

  const { refreshStudySessionAndContent } = useRefreshStudySessionAndContent();

  const isMounted = useRef(false);

  const [showSchoolSelector, setShowSchoolSelector] = useState<boolean>(false);
  const [errorMessage] = useState<string | null>(null);
  const [createExamStep, setCreateExamStep] = useState<number>(1);

  const navigate = useNavigate();

  useHotkeys('esc', () => {
    navigate('/');
  });

  // Show school selector if no school is selected for user
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      if (!userSchool) {
        setShowSchoolSelector(true);
      }
    }
  }, [userSchool]);

  // Fetch lectures on mount unless we are in create mode
  useEffect(() => {
    if (!isCreateMode && primaryExam?.id) {
      getExamLectures(primaryExam.id);
    } else if (isCreateMode) {
      store.dispatch(clearLectures());
    }
  }, [isCreateMode, primaryExam?.id]);

  const refreshAndClose = async () => {
    if (shouldRefreshStudySession) {
      const resetStudySession = todaySession && !todaySession.isStarted;
      await refreshStudySessionAndContent(resetStudySession);
    }
    if (isMounted.current) {
      navigate('/');
    }
  };

  return (
    <Page classes={`exam-editor ${isCreateMode ? 'exam-editor--create' : ''}`}>
      {errorMessage && (
        <div
          className="error-message"
          style={{ color: 'red', marginBottom: '1rem' }}
        >
          {errorMessage}
        </div>
      )}
      {isCreateMode && (
        <div className="create-exam">
          <div className="create-exam_content">
            {createExamStep === 1 && (
              <div className="create-exam-step create-exam-step--1">
                <div className="create-exam-step_content">
                  <h1>
                    Create exam
                    <span className="beta-badge">BETA</span>
                  </h1>
                  <p className="m-b-1-50">
                    Let's start by adding some details about the exam.
                  </p>
                  <div className="exam-info">
                    <SchoolExamInfo
                      isCreateMode={true}
                      newExamCreated={examCreated}
                      setExamCreated={setExamCreated}
                      setShouldRefreshStudySession={
                        setShouldRefreshStudySession
                      }
                    />
                    {showSchoolSelector && !primaryExam?.verified && (
                      <>
                        <InputLabel
                          id="medical-school-selection"
                          className="m-t-1-50 m-b-0-50"
                        >
                          Medical School
                        </InputLabel>
                        <SchoolSelector />
                      </>
                    )}
                  </div>
                </div>
                <div className="create-exam-step_actions">
                  <button
                    className="button button--glass"
                    onClick={refreshAndClose}
                  >
                    Cancel
                  </button>
                  <button
                    className="button button--glass"
                    onClick={() => setCreateExamStep(2)}
                    disabled={isCreateMode && !examCreated}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
            {createExamStep === 2 && (
              <div className="create-exam-step create-exam-step--2">
                <div className="create-exam-step_content">
                  <h1>Add lectures</h1>
                  <p className="m-b-1-50">
                    Ora creates daily assignments based on your lecture
                    schedule.
                  </p>
                  <LectureScheduleEditor
                    setShouldRefreshStudySession={setShouldRefreshStudySession}
                  />
                </div>
                <div className="create-exam-step_actions">
                  <button
                    className="button button--glass"
                    onClick={() => setCreateExamStep(1)}
                  >
                    Back
                  </button>
                  <button
                    className="button button--glass"
                    onClick={refreshAndClose}
                    disabled={isCreateMode && !examCreated}
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {!isCreateMode && (
        <div className="edit-exam">
          <div className="edit-exam_content">
            <SchoolExamInfo
              isCreateMode={false}
              setShouldRefreshStudySession={setShouldRefreshStudySession}
              closeSecondaryScreen={() => navigate('/')}
            />
            <LectureScheduleEditor
              setShouldRefreshStudySession={setShouldRefreshStudySession}
            />
          </div>
        </div>
      )}
    </Page>
  );
};

export default ExamEditor;
